import React, { PropsWithChildren, ReactElement, ReactNode } from 'react';

import {
  CouponContextProvider,
  CouponContextValue,
  ImageMapBrazeContextProvider,
  ImageMapBrazeContextValue,
  ImageMapShareContextProvider,
  ImageMapShareContextValue,
  RaffleContextProvider,
  RaffleContextValue,
  StoreItemsContextProvider,
  StoreItemsContextValue,
} from './external';
import {
  AccordionContextProvider,
  AccordionContextValue,
  BlockContextProvider,
  BlockContextValue,
  LinkContextProvider,
  LinkContextValue,
  StickyTabContextProvider,
  StickyTabContextValue,
  TabContextProvider,
  TabContextValue,
} from './internal';

type ProviderType<T> = ({ children, value }: PropsWithChildren<{
  value?: T,
}>) => JSX.Element;

export interface ContextValue {
  link?: LinkContextValue;
  block?: BlockContextValue;
  tab?: TabContextValue;
  accordion?: AccordionContextValue;
  coupon?: CouponContextValue;
  raffle?: RaffleContextValue;
  storeItems?: StoreItemsContextValue;
  stickyTab?: StickyTabContextValue;
  imageMapShare?: ImageMapShareContextValue;
  imageMapBraze?: ImageMapBrazeContextValue;
}

function provider<T>(provider: ProviderType<T>, props?: T): [ProviderType<T>, T | undefined] {
  return [provider, props];
}

interface ProviderComposerProps<T> {
  providers: [ProviderType<T>, T][];
  children: ReactNode;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function ProviderComposer(props: ProviderComposerProps<any>): ReactElement {
  const { providers = [], children } = props;

  return (
    <>
      { providers.reduceRight((acc, Comp) => {
        const [Provider, value] = Comp;
        return <Provider value={value}>{ acc }</Provider>;
      }, children) }
    </>
  );
}

export function EditorComponentsProvider({
  contexts,
  children,
}: PropsWithChildren<{ contexts: ContextValue }>): ReactElement {
  const {
    link,
    block,
    tab,
    accordion,
    coupon,
    raffle,
    storeItems,
    stickyTab,
    imageMapShare,
    imageMapBraze,
  } = contexts;

  return (
    <ProviderComposer
      providers={[
        provider(LinkContextProvider, link),
        provider(BlockContextProvider, block),
        provider(TabContextProvider, tab),
        provider(AccordionContextProvider, accordion),
        provider(CouponContextProvider, coupon),
        provider(RaffleContextProvider, raffle),
        provider(StoreItemsContextProvider, storeItems),
        provider(StickyTabContextProvider, stickyTab),
        provider(ImageMapShareContextProvider, imageMapShare),
        provider(ImageMapBrazeContextProvider, imageMapBraze),
      ]}
    >
      { children }
    </ProviderComposer>
  );
}
